import type { CartItem } from '../cartSlice';

import type { CspCatalogTreeNode } from '@/shared/types/userStandard';

export type CatalogCheckState = 'on' | 'partial' | 'off';

export function isLeafNode(node: CspCatalogTreeNode): boolean {
  return node.children.length === 0;
}

/** Count of all descendants (excluding the node itself). */
export function countDescendants(node: CspCatalogTreeNode): number {
  let total = 0;
  for (const child of node.children) total += 1 + countDescendants(child);
  return total;
}

/** Catalog ids of the node and every descendant. */
export function subtreeIds(node: CspCatalogTreeNode): string[] {
  const ids = [node.id];
  for (const child of node.children) ids.push(...subtreeIds(child));
  return ids;
}

/** Catalog ids of every descendant (excluding the node itself). */
export function descendantIds(node: CspCatalogTreeNode): string[] {
  const ids: string[] = [];
  for (const child of node.children) ids.push(...subtreeIds(child));
  return ids;
}

/** Flat id → tree-node index for a loaded set tree. */
export function indexCatalogTreeById(nodes: CspCatalogTreeNode[]): Map<string, CspCatalogTreeNode> {
  const map = new Map<string, CspCatalogTreeNode>();
  const walk = (node: CspCatalogTreeNode) => {
    map.set(node.id, node);
    for (const child of node.children) walk(child);
  };
  for (const node of nodes) walk(node);
  return map;
}

function addKeptSubtree(node: CspCatalogTreeNode, excluded: ReadonlySet<string>, out: Set<string>) {
  if (excluded.has(node.id)) return; // excluding a node drops it and everything beneath it
  out.add(node.id);
  for (const child of node.children) addKeptSubtree(child, excluded, out);
}

/**
 * Expand the cart's roots (minus their exclusions) and single picks into the concrete set of catalog
 * ids that are effectively "on" within the given loaded tree. The cart stays compact (roots +
 * exclusions); this expansion lives only in render memory.
 */
export function computeEffectiveSelected(
  nodes: CspCatalogTreeNode[],
  items: Record<string, CartItem>
): Set<string> {
  const index = indexCatalogTreeById(nodes);
  const effective = new Set<string>();
  for (const item of Object.values(items)) {
    if (!item.withDescendants) {
      effective.add(item.node.id);
      continue;
    }
    const root = index.get(item.node.id);
    if (!root) {
      // Root belongs to a different set's tree than the one rendered here.
      effective.add(item.node.id);
      continue;
    }
    addKeptSubtree(root, new Set(item.excludedIds), effective);
  }
  return effective;
}

/**
 * Tri-state for every node in a loaded tree, in one pass. A node is `on` when it is effectively
 * selected, `partial` when it is not but some descendant is, else `off`.
 */
export function computeCatalogCheckStates(
  nodes: CspCatalogTreeNode[],
  items: Record<string, CartItem>
): Map<string, CatalogCheckState> {
  const effective = computeEffectiveSelected(nodes, items);
  const states = new Map<string, CatalogCheckState>();

  const visit = (node: CspCatalogTreeNode): CatalogCheckState => {
    if (effective.has(node.id)) {
      for (const child of node.children) visit(child);
      states.set(node.id, 'on');
      return 'on';
    }
    let hasSelected = false;
    for (const child of node.children) {
      if (visit(child) !== 'off') hasSelected = true;
    }
    const state: CatalogCheckState = hasSelected ? 'partial' : 'off';
    states.set(node.id, state);
    return state;
  };

  for (const node of nodes) visit(node);
  return states;
}

export function computeTreeCheckState(
  nodes: CspCatalogTreeNode[],
  items: Record<string, CartItem>
): CatalogCheckState {
  const effective = computeEffectiveSelected(nodes, items);
  let total = 0;
  let selected = 0;

  const visit = (node: CspCatalogTreeNode) => {
    total += 1;
    if (effective.has(node.id)) selected += 1;
    for (const child of node.children) visit(child);
  };

  for (const node of nodes) visit(node);

  if (selected === 0) return 'off';
  return selected === total ? 'on' : 'partial';
}

/**
 * The deepest whole-branch cart root (in the same set) that contains `node`, or null. Used to route a
 * toggle inside a selected branch into that root's exclusion set rather than a new cart entry.
 */
export function findCoveringRoot(
  items: Record<string, CartItem>,
  node: CspCatalogTreeNode
): CartItem | null {
  let best: CartItem | null = null;
  for (const item of Object.values(items)) {
    if (!item.withDescendants) continue;
    if (item.node.id === node.id) continue;
    if (item.node.standard_set_id !== node.standard_set_id) continue;
    if (!node.ancestor_csp_ids.includes(item.node.csp_id)) continue;
    if (!best || item.node.depth > best.node.depth) best = item;
  }
  return best;
}
